import type { AnchorDecoration } from '@wix/ricos';
import type {
  AudioNode,
  Decoration,
  LinkDecoration,
  Media,
  RicosDocument,
  RicosDocumentNode,
  RicosDocumentNodeType,
} from '@wix/ricos/schema-next';
import { Link_Target } from '@wix/ricos/schema-next';

import { isDefined } from '../../common/is-defined';
import { unreachable } from '../../common/unreachable';
import {
  audioAttrs,
  buttonAttrs,
  collapsibleListAttrs,
  fileAttrs,
  gifAttrs,
  htmlAttrs,
  imageAttrs,
  tableAttrs,
  tableCellAttrs,
} from './attributes';
import type { RcmlBlockNode, RcmlNode, RcmlTextNode } from './rcml-node';

export const toRcml = (
  content: RicosDocument,
  serialize: (rcml: RcmlNode[]) => string = serializeRcml
): string => {
  const _toRcml = (node: RicosDocumentNode): RcmlNode => {
    const converter = toRcmlConverters[node.type];
    return converter(node, nodes => nodes.map(_toRcml));
  };
  const rcml = content.nodes.map(_toRcml);
  return serialize(rcml);
};

const generateUniqueId = () => {
  return Array.from(crypto.getRandomValues(new Uint8Array(3)))
    .map(v => v.toString(36))
    .join('');
};

export const parseXmlNode = (node: Node): RcmlNode[] => {
  if (node.nodeType === Node.TEXT_NODE) {
    if (!node.textContent?.trim()) {
      return [];
    }
    return [
      {
        type: 'span',
        text: node.textContent.trim(),
      },
    ];
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    if (element.tagName.toLowerCase() === 'span') {
      return [
        {
          type: 'span',
          class: element.getAttribute('class') || undefined,
          text: element.textContent || '',
          href: element.getAttribute('href') || undefined,
        },
      ];
    }

    const attributes: Record<string, string> = {};
    Array.from(element.attributes).forEach(attr => {
      attributes[attr.name] = attr.value;
    });

    const children: RcmlNode[] = [];
    element.childNodes.forEach(child => {
      children.push(...parseXmlNode(child));
    });

    return [
      {
        type: element.tagName.toLowerCase() as any,
        attributes,
        children,
      },
    ];
  }

  return [];
};

export type XmlNode = {
  type: string;
  attributes: Record<string, string>;
  children: XmlNode[];
  class?: string;
  text?: string;
  href?: string;
  rel?: string;
  target?: string;
};

export const fromRcml = (rcmlNodes: RcmlNode[]): RicosDocumentNode[] => {
  const _fromRcml = (node: RcmlNode): any => {
    if (node.type === 'span') {
      return {
        type: 'TEXT',
        id: '',
        textData: {
          text: unescapeHTML(node.text),
          decorations: classesToDecorations(node),
        },
      };
    }

    const baseNode = {
      // dont use dashes in this id as the ai might start flaking when using it for ranges
      id: node.attributes.id ?? `ai${generateUniqueId()}`,
    };

    switch (node.type) {
      case 'p':
        return { ...baseNode, type: 'PARAGRAPH', nodes: node.children.map(_fromRcml) };
      case 'br': {
        return {
          type: 'TEXT',
          id: '',
          textData: {
            text: '\n',
            decorations: [],
          },
        };
      }
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
        return {
          ...baseNode,
          type: 'HEADING',
          headingData: { level: parseInt(node.type.charAt(1)) },
          nodes: node.children.map(_fromRcml),
        };
      case 'ul':
        return { ...baseNode, type: 'BULLETED_LIST', nodes: node.children.map(_fromRcml) };
      case 'ol':
        return { ...baseNode, type: 'ORDERED_LIST', nodes: node.children.map(_fromRcml) };
      case 'li':
        return { ...baseNode, type: 'LIST_ITEM', nodes: node.children.map(_fromRcml) };
      case 'quote':
        return { ...baseNode, type: 'BLOCKQUOTE', nodes: node.children.map(_fromRcml) };
      case 'code_block':
        return { ...baseNode, type: 'CODE_BLOCK', nodes: node.children.map(_fromRcml) };
      case 'video':
        return { ...baseNode, type: 'VIDEO', nodes: node.children.map(_fromRcml) };
      case 'divider':
        return {
          ...baseNode,
          type: 'DIVIDER',
          dividerData: {
            width: node.attributes.width?.toUpperCase(),
            alignment: node.attributes.alignment?.toUpperCase(),
            lineStyle: node.attributes.linestyle?.toUpperCase(),
          },
        };
      case 'file': {
        const fileNode = {
          ...baseNode,
          type: 'FILE',
        } as RicosDocumentNode;

        fileAttrs.des(fileNode, node.attributes);
        return fileNode;
      }
      case 'gallery':
        return {
          ...baseNode,
          type: 'GALLERY',
          galleryData: {
            items: node.children
              .filter((child): child is RcmlBlockNode => child.type === 'gallery_item')
              .map(item => {
                const itemType = item.attributes.type;
                const media = {
                  src: {
                    url: item.attributes.src,
                  },
                  width: item.attributes.originalwidth && parseInt(item.attributes.originalwidth),
                  height:
                    item.attributes.originalheight && parseInt(item.attributes.originalheight),
                };

                return {
                  title: item.attributes.caption,
                  altText: item.attributes.alt,
                  ...(itemType === 'image' ? { image: { media } } : {}),
                  ...(itemType === 'video' ? { video: { media } } : {}),
                };
              }),
            options: {
              layout: {
                type: (node.attributes.type || 'grid').toUpperCase(),
              },
            },
          },
        };
      case 'gif': {
        const gifNode = { ...baseNode, type: 'GIF', nodes: [], gifData: {} } as RicosDocumentNode;
        gifAttrs.des(gifNode, node.attributes);
        return gifNode;
      }
      case 'html': {
        const htmlNode = {
          ...baseNode,
          type: 'HTML',
          htmlData: {
            html: serializeRcml(node.children),
          },
        } as RicosDocumentNode;
        htmlAttrs.des(htmlNode, node.attributes);
        return htmlNode;
      }
      // @ts-ignore the parser parses it as img
      case 'img':
      case 'image': {
        const imageNode = {
          ...baseNode,
          type: 'IMAGE',
          nodes: node.children.map(_fromRcml),
        } as RicosDocumentNode;
        imageAttrs.des(imageNode, node.attributes);
        return imageNode;
      }
      case 'link_preview':
        return { ...baseNode, type: 'LINK_PREVIEW', nodes: node.children.map(_fromRcml) };
      case 'poll':
        return { ...baseNode, type: 'POLL' };
      case 'app_embed':
        return { ...baseNode, type: 'APP_EMBED' };
      case 'button': {
        const buttonNode = {
          ...baseNode,
          type: 'BUTTON',
          buttonData: { type: 'LINK' },
        } as RicosDocumentNode;
        buttonAttrs.des(buttonNode, node.attributes);
        return buttonNode;
      }
      case 'collapsible_list': {
        const collapsibleListNode = {
          ...baseNode,
          type: 'COLLAPSIBLE_LIST',
          nodes: node.children.map(_fromRcml),
        } as RicosDocumentNode;
        collapsibleListAttrs.des(collapsibleListNode, node.attributes);
        return collapsibleListNode;
      }
      case 'collapsible_item':
        return { ...baseNode, type: 'COLLAPSIBLE_ITEM', nodes: node.children.map(_fromRcml) };
      case 'collapsible_item_title':
        return { ...baseNode, type: 'COLLAPSIBLE_ITEM_TITLE', nodes: node.children.map(_fromRcml) };
      case 'collapsible_item_body':
        return { ...baseNode, type: 'COLLAPSIBLE_ITEM_BODY', nodes: node.children.map(_fromRcml) };
      case 'embed':
        return { ...baseNode, type: 'EMBED' };
      case 'table': {
        const tableNode = {
          ...baseNode,
          type: 'TABLE',
          nodes: node.children.map(_fromRcml),
        } as RicosDocumentNode;
        tableAttrs.des(tableNode, node.attributes);
        return tableNode;
      }
      case 'table_row':
        return { ...baseNode, type: 'TABLE_ROW', nodes: node.children.map(_fromRcml) };
      case 'table_cell': {
        const tableCell = {
          ...baseNode,
          type: 'TABLE_CELL',
          nodes: node.children.map(_fromRcml),
        } as RicosDocumentNode;
        tableCellAttrs.des(tableCell, node.attributes);
        return tableCell;
      }
      case 'external':
        return { ...baseNode, type: 'EXTERNAL', nodes: node.children.map(_fromRcml) };
      case 'audio': {
        const audioNode = {
          ...baseNode,
          type: 'AUDIO',
        } as RicosDocumentNode;

        audioAttrs.des(audioNode, node.attributes);
        return audioNode;
      }
      case 'caption':
        return { ...baseNode, type: 'CAPTION', nodes: node.children.map(_fromRcml) };
      case 'layout':
        return { ...baseNode, type: 'LAYOUT', nodes: node.children.map(_fromRcml) };
      case 'layout_cell': {
        const colSpan = node.attributes.colspan && parseInt(node.attributes.colspan);
        const layoutCellData = colSpan ? { colSpan } : undefined;
        return {
          ...baseNode,
          type: 'LAYOUT_CELL',
          layoutCellData,
          nodes: node.children.map(_fromRcml),
        };
      }
      case 'gallery_item':
        console.error('gallery_item should not be a top-level node');
        return undefined;

      case 'spotify_audio': {
        const url = node.attributes.url;
        if (!url) {
          console.error('spotify_audio should have url attribute');
          return undefined;
        }
        return createRicosSpotifyAudioNode(url);
      }

      default:
        // @ts-expect-error
        console.error(new Error(`Unsupported node type: ${node.type}`));
        return undefined;
    }
  };

  return rcmlNodes.map(_fromRcml).filter(isDefined);
};

const classesToDecorations = (node: RcmlTextNode): Decoration[] => {
  // @ts-ignore
  const decorations: Decoration[] = (node.class ?? '')
    .split(' ')
    .filter(Boolean)
    .map(className => {
      if (className === 'bold') return { type: 'BOLD' };
      if (className === 'italic') return { type: 'ITALIC' };
      if (className === 'underline') return { type: 'UNDERLINE' };
      if (className === 'link') return { type: 'UNDERLINE' };
      if (className === 'spoiler') return { type: 'SPOILER' };
      if (className === 'anchor') return { type: 'ANCHOR' };
      if (className.startsWith('mention-')) {
        return {
          type: 'MENTION',
          mentionData: { id: className.replace('mention-', '') },
        };
      }
      if (className.startsWith('fg-color-')) {
        return {
          type: 'COLOR',
          colorData: { foreground: className.replace('fg-color-', '') },
        };
      }
      if (className.startsWith('bg-color-')) {
        return {
          type: 'COLOR',
          colorData: { background: className.replace('bg-color-', '') },
        };
      }
      if (className.startsWith('font-size-')) {
        const [value, unit] =
          className
            .replace('font-size-', '')
            .match(/(\d+)(\D+)?/)
            ?.slice(1) ?? [];
        return {
          type: 'FONT_SIZE',
          fontSizeData: { value: parseInt(value), unit: unit || ('px' as any) },
        };
      }
      return undefined;
      // Hallucinating with non-existing classes
      // throw new Error(`Unknown decoration class: ${className}`);
    })
    .filter(d => typeof d !== 'undefined');

  if (node.href) {
    if (node.href.startsWith('#')) {
      decorations.push({ type: 'ANCHOR', anchorData: { anchor: node.href.slice(1) } });
    } else {
      const { rel } = node;
      const finalRel: { noreferrer?: boolean; sponsored?: boolean; nofollow?: boolean } = {};

      if (rel) {
        finalRel.noreferrer = !rel.includes('withreferrer');
        finalRel.nofollow = rel.includes('nofollow');
        finalRel.sponsored = rel.includes('sponsored');
      }

      const target = node.target === 'self' ? Link_Target.TOP : Link_Target.BLANK;

      decorations.push({
        type: 'LINK',
        linkData: { link: { url: node.href, rel: finalRel, target } },
      });
    }
  }
  return decorations;
};

const toRcmlConverters: Record<
  RicosDocumentNodeType,
  (node: RicosDocumentNode, visit: (nodes: RicosDocumentNode[]) => RcmlNode[]) => RcmlNode
> = {
  PARAGRAPH: (node, visit) => {
    return {
      type: 'p',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
  },
  TEXT: (node, visit) => {
    if (node.type !== 'TEXT') {
      throw new Error('Incorrect node type');
    }
    const linkDecoration: LinkDecoration | undefined = node.textData.decorations?.find(
      d => d.type === 'LINK'
    ) as LinkDecoration;

    const anchorDecoration: AnchorDecoration | undefined = node.textData.decorations?.find(
      d => d.type === 'ANCHOR'
    ) as AnchorDecoration;

    const relValue = Object.entries(linkDecoration?.linkData?.link?.rel || {})
      .reduce((acc, [key, value]) => {
        if (key === 'noreferrer') {
          return value ? acc : `${acc} withreferrer`;
        }
        return value ? `${acc} ${key}` : acc;
      }, '')
      .trim();

    return {
      type: 'span',
      class: decorationsToClasses(node.textData.decorations ?? []),
      href: linkDecoration?.linkData?.link?.url
        ? linkDecoration.linkData.link.url
        : anchorDecoration?.anchorData?.anchor
        ? `#${anchorDecoration.anchorData.anchor}`
        : undefined,
      rel: relValue,
      target: linkDecoration?.linkData?.link?.target !== Link_Target.BLANK ? 'self' : 'blank',
      text: escapeHTML(node.textData.text),
      children: [],
    };
  },
  HEADING: (node, visit) => {
    if (node.type !== 'HEADING') {
      throw new Error('Incorrect node type');
    }

    return {
      type: ('h' + (node.headingData?.level ?? '1')) as any,
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
  },
  BULLETED_LIST: (node, visit) => {
    return {
      type: 'ul',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
  },
  ORDERED_LIST: (node, visit) => {
    return {
      type: 'ol',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
  },
  LIST_ITEM: (node, visit) => {
    return {
      type: 'li',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
  },
  BLOCKQUOTE: (node, visit) => {
    return {
      type: 'quote',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
  },
  CODE_BLOCK: (node, visit) => {
    return {
      type: 'code_block',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
  },
  VIDEO: (node, visit) => {
    return {
      type: 'video',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
  },
  DIVIDER: (node, visit) => {
    if (node.type !== 'DIVIDER') {
      throw new Error('Incorrect node type');
    }
    return {
      type: 'divider',
      attributes: {
        id: node.id!,
        width: node.dividerData?.width?.toLowerCase(),
        alignment: node.dividerData?.alignment?.toLowerCase(),
        linestyle: node.dividerData?.lineStyle?.toLowerCase(),
      },
      children: visit(node.nodes ?? []),
    };
  },

  FILE: (node, visit) => {
    if (node.type !== 'FILE') {
      throw new Error('Incorrect node type');
    }

    const rcmlNode: RcmlBlockNode = {
      type: 'file',
      attributes: {
        id: node.id!,
        type: node.fileData?.type,
        name: node.fileData?.name,
        fileSize: node.fileData?.size?.toString(),
        fileSizeInKb: node.fileData?.sizeInKb,
        size: node.fileData?.containerData?.width?.size?.toLocaleLowerCase(),
        alignment: node.fileData?.containerData?.alignment?.toLocaleLowerCase(),
        textWrap: (node.fileData?.containerData?.textWrap ?? false).toString(),
        url: node.fileData?.src?.url,
        private: (node.fileData?.src?.private ?? false).toString(),
      },
      children: visit(node.nodes ?? []),
    };
    fileAttrs.ser(rcmlNode.attributes, node);
    return rcmlNode;
  },
  GALLERY: (node, visit) => {
    if (node.type !== 'GALLERY') {
      throw new Error('Incorrect node type');
    }
    return {
      type: 'gallery',
      attributes: {
        id: node.id!,
        type: node.galleryData?.options?.layout?.type?.toLowerCase() || 'grid',
      },
      children: node.galleryData?.items.map((item, index) => {
        const attributes = item.image
          ? {
              type: 'image',
              ...toMediaSrc(item.image.media),
            }
          : item.video
          ? {
              type: 'video',
              ...toMediaSrc(item.video.media),
            }
          : {};

        return {
          type: 'gallery_item',
          children: [],
          attributes: {
            ...attributes,
            alt: item.altText,
            caption: item.title,
          },
        };
      }),
    };
  },
  GIF: (node, visit) => {
    const rcmlNode = {
      type: 'gif',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    } as RcmlBlockNode;
    gifAttrs.ser(rcmlNode.attributes, node);
    return rcmlNode;
  },
  HTML: (node, visit) => {
    if (node.type !== 'HTML') {
      throw new Error('Incorrect node type');
    }
    const htmlNode = {
      type: 'html',
      attributes: { id: node.id! },
      children: [{ type: 'span', text: node.htmlData.html || '' }],
    } as RcmlBlockNode;
    htmlAttrs.ser(htmlNode.attributes, node);
    return htmlNode;
  },
  IMAGE: (node, visit) => {
    if (node.type !== 'IMAGE') {
      throw new Error('Incorrect node type');
    }
    const rcmlNode = {
      type: 'image',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    } as RcmlBlockNode;
    imageAttrs.ser(rcmlNode.attributes, node);
    return rcmlNode;
  },
  LINK_PREVIEW: (node, visit) => {
    return {
      type: 'link_preview',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
  },
  POLL: (node, visit) => {
    return {
      type: 'poll',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
  },
  APP_EMBED: (node, visit) => {
    return {
      type: 'app_embed',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
  },
  BUTTON: (node, visit) => {
    if (node.type !== 'BUTTON') {
      throw new Error('Incorrect node type');
    }
    const rcmlNode: RcmlBlockNode = {
      type: 'button',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
    buttonAttrs.ser(rcmlNode.attributes, node);
    return rcmlNode;
  },
  COLLAPSIBLE_LIST: (node, visit) => {
    if (node.type !== 'COLLAPSIBLE_LIST') {
      throw new Error('Incorrect node type');
    }
    const rcmlNode: RcmlBlockNode = {
      type: 'collapsible_list',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
    collapsibleListAttrs.ser(rcmlNode.attributes, node);
    return rcmlNode;
  },
  COLLAPSIBLE_ITEM: (node, visit) => {
    return {
      type: 'collapsible_item',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
  },
  COLLAPSIBLE_ITEM_TITLE: (node, visit) => {
    return {
      type: 'collapsible_item_title',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
  },
  COLLAPSIBLE_ITEM_BODY: (node, visit) => {
    return {
      type: 'collapsible_item_body',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
  },
  TABLE: (node, visit) => {
    const rcmlNode: RcmlBlockNode = {
      type: 'table',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
    tableAttrs.ser(rcmlNode.attributes, node);
    return rcmlNode;
  },
  EMBED: (node, visit) => {
    return {
      type: 'embed',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
  },
  TABLE_CELL: (node, visit) => {
    const rcmlNode: RcmlBlockNode = {
      type: 'table_cell',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
    tableCellAttrs.ser(rcmlNode.attributes, node);
    return rcmlNode;
  },
  TABLE_ROW: (node, visit) => {
    return {
      type: 'table_row',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
  },
  EXTERNAL: (node, visit) => {
    return {
      type: 'external',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
  },
  AUDIO: (node, visit) => {
    if (node.type !== 'AUDIO') {
      throw new Error('Incorrect node type');
    }

    if (isRicosSpotifyAudioNode(node)) {
      return {
        type: 'spotify_audio',
        attributes: { id: node.id!, url: (node as AudioNode).audioData.audio!.src!.url! },
        children: visit(node.nodes ?? []),
      };
    }

    const rcmlNode: RcmlBlockNode = {
      type: 'audio',
      attributes: {
        id: node.id!,
        url: node.audioData.audio?.src.url,
        duration: node.audioData.audio?.duration?.toString(),
        name: node.audioData.name,
        authorName: node.audioData.authorName,
        disableDownload: (node.audioData.disableDownload ?? false).toString(),
        textWrap: (node.audioData.containerData?.textWrap ?? false).toString(),
        alignment: node.audioData.containerData?.alignment?.toLowerCase(),
        width: node.audioData.containerData?.width?.size?.toLowerCase(),
        customWidth: node.audioData.containerData?.width?.custom,
      },
      children: visit(node.nodes ?? []),
    };
    audioAttrs.ser(rcmlNode.attributes, node);
    return rcmlNode;
  },
  CAPTION: (node, visit) => {
    return {
      type: 'caption',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
  },
  LAYOUT: (node, visit) => {
    return {
      type: 'layout',
      attributes: { id: node.id! },
      children: visit(node.nodes ?? []),
    };
  },
  LAYOUT_CELL: (node, visit) => {
    if (node.type !== 'LAYOUT_CELL') {
      throw new Error('Incorrect node type');
    }
    return {
      type: 'layout_cell',
      attributes: { id: node.id!, colspan: node.layoutCellData?.colSpan?.toString() },
      children: visit(node.nodes ?? []),
    };
  },
  MAP: (node, visit) => {
    throw new Error('Not implemented MAP');
  },
  UNRECOGNIZED: (node, visit) => {
    throw new Error('Not implemented UNRECOGNIZED');
  },
};

// Convert RcmlNode to HTML string
export const serializeRcml = (rcmlNodes: RcmlNode[], level = 0): string => {
  const levelPrefix = '  '.repeat(level);
  return rcmlNodes
    .map(node => {
      if (node.type === 'span') {
        if (!node.class && !node.href) {
          return `${node.text}`;
        } else {
          const attrs = { class: node.class, href: node.href };
          const attrsString = Object.entries(attrs)
            .filter(([, value]) => value)
            .map(([key, value]) => `${key}="${value}"`)
            .join(' ');
          return `<span ${attrsString}>${node.text}</span>`;
        }
      }
      const attrsArray = Object.entries(node.attributes)
        .filter(([key, value]) => value !== undefined)
        .map(([key, value]) => `${key}="${value}"`);
      const attrsOneLine = attrsArray.join(' ');
      const attrsMultiline = attrsArray.map(attr => `${levelPrefix}  ${attr}`).join('\n');
      const attrs = attrsOneLine.length > 80 ? `\n${attrsMultiline}` : attrsOneLine;
      if (!node.children || !node.children.length) {
        if (attrs.length > 0) {
          return `${levelPrefix}<${node.type} ${attrs} />\n`;
        } else {
          return `${levelPrefix}<${node.type}/>\n`;
        }
      }

      const children = serializeRcml(node.children, level + 1);
      const normalizedAttrs = attrs.length > 0 ? ` ${attrs}` : '';
      if (isTextContainer(node.type)) {
        return `${levelPrefix}<${node.type}${normalizedAttrs}>${children}</${node.type}>\n`;
      }
      return `${levelPrefix}<${node.type}${normalizedAttrs}>\n${children}${levelPrefix}</${node.type}>\n`;
    })
    .join('');
};

const decorationsToClasses = (decorations: Decoration[]): string => {
  return decorations
    .filter(d => d.type !== 'LINK' && d.type !== 'ANCHOR')
    .map(decoration => {
      switch (decoration.type) {
        case 'BOLD':
          return 'bold';
        case 'ITALIC':
          return 'italic';
        case 'UNDERLINE':
          return 'underline';
        case 'SPOILER':
          return 'spoiler';
        case 'MENTION':
          return 'mention-' + decoration.mentionData?.id;
        case 'COLOR': {
          const { foreground, background } = decoration.colorData ?? {};
          const fg = foreground ? 'fg-color-' + decoration.colorData?.foreground : '';
          const bg = background ? 'bg-color-' + decoration.colorData?.background : '';
          return (fg + ' ' + bg).trim();
        }
        case 'FONT_SIZE':
          return (
            'font-size-' + decoration.fontSizeData?.value + (decoration.fontSizeData?.unit ?? 'px')
          );
        default:
          // @ts-ignore
          console.error(unreachable(decoration));
          return '';
      }
    })
    .join(' ');
};

const filterUndefined = <T>(object: Record<string, T | undefined>): Record<string, T> => {
  return Object.fromEntries(
    Object.entries(object).filter(([, value]) => value !== undefined)
  ) as any;
};

function escapeHTML(str) {
  return str.replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function unescapeHTML(str) {
  return str.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
}

const toMediaSrc = (
  media: Media
): { src: string; originalwidth?: string; originalheight?: string } => {
  const { src, width, height } = media;
  const idOrUrl = src.id ?? src.url ?? '';
  const url = /^https?:\/\//.test(idOrUrl)
    ? idOrUrl
    : `https://static.wixstatic.com/media/${idOrUrl}`;
  return {
    src: url,
    originalwidth: width ? String(width) : undefined,
    originalheight: height ? String(height) : undefined,
  };
};

export const isTextContainer = (tag: string) =>
  [
    'span',
    'explain',
    'think',
    'p',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'code_block',
    'caption',
  ].includes(tag);

const createSpotifyEmbedHtmlString = (trackUrl: string) => {
  const embedSrc = trackUrl.replace('open.spotify.com/track/', 'open.spotify.com/embed/track/');
  return `<iframe width="100%" height="80" frameborder="0" allowfullscreen allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" src="${embedSrc}"></iframe>`;
};

const isRicosSpotifyAudioNode = (node: RicosDocumentNode) => {
  return node.type === 'AUDIO'
    ? (node as AudioNode).audioData?.audio?.src?.url?.startsWith('https://open.spotify.com/track/')
    : false;
};

const createRicosSpotifyAudioNode = (url: string): AudioNode => {
  return {
    type: 'AUDIO',
    audioData: {
      audio: {
        src: {
          url,
        },
      },
      html: createSpotifyEmbedHtmlString(url),
    },
  };
};
